/**
 * CancelConfirmModal - Confirmation dialog for booking cancellation
 */

import { useState } from 'react';
import { Button } from '../ui/button';
import { getApiBase } from '../../lib/apiBase';

const API_BASE = getApiBase();

interface BookingInfo {
  id: string;
  scheduledAt: string;
  vehicle: { registration: string; make: string; model: string } | null;
}

interface CancelConfirmModalProps {
  booking: BookingInfo;
  onClose: () => void;
  onSuccess: () => void;
}

export function CancelConfirmModal({ booking, onClose, onSuccess }: CancelConfirmModalProps) {
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const sessionId = localStorage.getItem('customer_session');

  const handleCancel = async () => {
    if (!sessionId) return;

    setSubmitting(true);
    setError(null);

    try {
      const res = await fetch(`${API_BASE}/api/customer-portal/bookings/${booking.id}/cancel`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Customer-Session': sessionId,
        },
        body: JSON.stringify({ reason: reason || undefined }),
      });

      const data = await res.json();
      if (!res.ok) {
        setError(data.error || 'Error al cancelar');
        return;
      }

      onSuccess();
    } catch {
      setError('Error de conexion');
    } finally {
      setSubmitting(false);
    }
  };

  const formatDate = (dateStr: string) =>
    new Date(dateStr).toLocaleString('es-ES', {
      weekday: 'long',
      day: 'numeric',
      month: 'long',
      hour: '2-digit',
      minute: '2-digit',
    });

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-2">Cancelar Cita</h2>
        <p className="text-sm text-gray-600 mb-4">
          ¿Está seguro de que desea cancelar esta cita?
        </p>

        <div className="bg-gray-50 rounded-md p-3 mb-4">
          <p className="text-sm font-medium">📅 {formatDate(booking.scheduledAt)}</p>
          {booking.vehicle && (
            <p className="text-sm text-gray-600">
              🚗 {booking.vehicle.registration} - {booking.vehicle.make} {booking.vehicle.model}
            </p>
          )}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Motivo (opcional)
          </label>
          <textarea
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="Indique el motivo de la cancelación..."
            className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm resize-none"
            rows={3}
            maxLength={500}
          />
        </div>

        {error && <p className="text-sm text-red-600 mt-3">{error}</p>}

        <div className="flex justify-end gap-2 mt-6">
          <Button variant="outline" onClick={onClose} disabled={submitting}>
            Volver
          </Button>
          <Button
            variant="destructive"
            onClick={handleCancel}
            disabled={submitting}
          >
            {submitting ? 'Cancelando...' : 'Confirmar Cancelación'}
          </Button>
        </div>
      </div>
    </div>
  );
}
